/**
 * Firestore write helpers for the seating plan.
 *
 * Per plan version:
 *   - /weddings/{wid}/seatingPlans/{planId}/tables/{tid}.guestIds[] / seatMap
 *   - /weddings/{wid}/guests/{gid}.seatingByPlan[planId]
 *
 * Every assignment / unassignment goes through a transaction so the two sides
 * never diverge under concurrent updates.
 */

import {
  addDoc,
  arrayRemove,
  arrayUnion,
  deleteDoc,
  runTransaction,
  serverTimestamp,
  updateDoc,
  writeBatch,
  type DocumentSnapshot,
  type Transaction,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type {
  Guest,
  RoomElement,
  SeatingCanvasInset,
  Table,
  TableShape,
} from "@amazing-wedding/shared-types";
import { guestSeatingFieldPath } from "@amazing-wedding/shared-types";
import { isGuestShownAsCompanion, getCanonicalCoupleIds } from "@/features/guests/guest-unit-rows";
import { SeatingError } from "./seating-errors";
import { REFERENCE_CANVAS } from "./constants";
import {
  computeSpaciousTableLayout,
  DEFAULT_SEATING_ROOM_WIDTH_M,
} from "@/features/seating/table-layout";
import {
  guestRef,
  planRoomElementRef,
  planRoomElementsCol,
  planTableRef,
  planTablesCol,
  seatingPlanRef,
} from "./firestore-paths";
import { updateSeatingPlan } from "./plan-operations";
import {
  applyWithinTableOccupiedMove,
  canPlaceParty,
  canPlaceCoupleParty,
  effectiveSeatMap,
  guestAppearsInSeatMap,
  hostGuestDocId,
  hostGuestIdsInSeatMap,
  parseOccupantKey,
  partyHostSeatIndex,
  placeGuestInSeatMap,
  placeCoupleInSeatMap,
  removeGuestFromSeatMap,
  removeCoupleFromSeatMap,
  swapCoupleMemberSeatsInSeatMap,
  swapPartyMemberSeatsInSeatMap,
} from "./seat-map";
import type { PlaceGuestOptions } from "./seat-map";
import type { SeatOccupantSwapMode } from "./seat-occupant-menu";
import type { OptimisticSeatPatch } from "./seating-optimistic";

export interface TableDraft {
  name: string;
  shape: TableShape;
  capacity: number;
  positionX: number;
  positionY: number;
  rotation?: number;
  isVip?: boolean;
  color?: string | null;
  notes?: string | null;
  hiddenSeats?: number[];
}

export type TablePatch = Partial<Omit<Table, "id" | "createdAt" | "updatedAt" | "guestIds">> & {
  seatMap?: (string | null)[];
  guestIds?: string[];
};

function seatingGuestUpdate(
  planId: string,
  tableId: string | null,
  seatNumber: number | null,
): Record<string, unknown> {
  return {
    [guestSeatingFieldPath(planId)]: { tableId, seatNumber },
    updatedAt: serverTimestamp(),
  };
}

function guestDocRef(weddingId: string, guestIdOrOccupantKey: string) {
  return guestRef(weddingId, hostGuestDocId(guestIdOrOccupantKey));
}

function tableFromSnap(snap: DocumentSnapshot): Table {
  const data = snap.data() as Omit<Table, "id">;
  return { id: snap.id, ...data, seatMap: data.seatMap ?? [] };
}

function assertNotLinkedCompanion(guest: Guest, allGuests: Guest[]): void {
  if (getCanonicalCoupleIds(allGuests).has(guest.id)) return;
  if (isGuestShownAsCompanion(guest, allGuests)) {
    throw new SeatingError("linkedCompanion");
  }
}

function assertSeatIndex(table: Table, seatIndex: number): void {
  const hidden = table.hiddenSeats ?? [];
  if (seatIndex < 0 || seatIndex >= table.capacity) {
    throw new SeatingError("seatOutOfRange");
  }
  if (hidden.includes(seatIndex)) {
    throw new SeatingError("seatDeactivated");
  }
}

function purgeGuestFromTableInTx(
  tx: Transaction,
  weddingId: string,
  planId: string,
  tableId: string,
  guestId: string,
  freshTable: Table,
  guestsById: Record<string, Guest>,
): void {
  const baseSeatMap = effectiveSeatMap(freshTable, guestsById);
  if (!guestAppearsInSeatMap(baseSeatMap, guestId)) return;
  const newSeatMap = removeGuestFromSeatMap(baseSeatMap, guestId);
  tx.update(planTableRef(weddingId, planId, tableId), {
    guestIds: arrayRemove(guestId),
    seatMap: newSeatMap,
    updatedAt: serverTimestamp(),
  });
}

function purgeCoupleFromTableInTx(
  tx: Transaction,
  weddingId: string,
  planId: string,
  tableId: string,
  groomId: string,
  brideId: string,
  freshTable: Table,
  guestsById: Record<string, Guest>,
): void {
  const baseSeatMap = effectiveSeatMap(freshTable, guestsById);
  if (
    !guestAppearsInSeatMap(baseSeatMap, groomId) &&
    !guestAppearsInSeatMap(baseSeatMap, brideId)
  ) {
    return;
  }
  const newSeatMap = removeCoupleFromSeatMap(baseSeatMap, groomId, brideId);
  tx.update(planTableRef(weddingId, planId, tableId), {
    guestIds: arrayRemove(groomId, brideId),
    seatMap: newSeatMap,
    updatedAt: serverTimestamp(),
  });
}

function purgeGuestFromOtherTablesInTx(
  tx: Transaction,
  weddingId: string,
  planId: string,
  guestId: string,
  targetTableId: string,
  allTableIds: string[],
  freshTablesById: Map<string, Table>,
  guestsById: Record<string, Guest>,
): void {
  for (const tableId of allTableIds) {
    if (tableId === targetTableId) continue;
    const fresh = freshTablesById.get(tableId);
    if (!fresh) continue;
    purgeGuestFromTableInTx(tx, weddingId, planId, tableId, guestId, fresh, guestsById);
  }
}

function purgeCoupleFromOtherTablesInTx(
  tx: Transaction,
  weddingId: string,
  planId: string,
  groomId: string,
  brideId: string,
  targetTableId: string,
  allTableIds: string[],
  freshTablesById: Map<string, Table>,
  guestsById: Record<string, Guest>,
): void {
  for (const tableId of allTableIds) {
    if (tableId === targetTableId) continue;
    const fresh = freshTablesById.get(tableId);
    if (!fresh) continue;
    purgeCoupleFromTableInTx(tx, weddingId, planId, tableId, groomId, brideId, fresh, guestsById);
  }
}

function purgeGuestFromAllTablesInTx(
  tx: Transaction,
  weddingId: string,
  planId: string,
  guestId: string,
  allTableIds: string[],
  freshTablesById: Map<string, Table>,
  guestsById: Record<string, Guest>,
): void {
  for (const tableId of allTableIds) {
    const fresh = freshTablesById.get(tableId);
    if (!fresh) continue;
    purgeGuestFromTableInTx(tx, weddingId, planId, tableId, guestId, fresh, guestsById);
  }
}

function purgeCoupleFromAllTablesInTx(
  tx: Transaction,
  weddingId: string,
  planId: string,
  groomId: string,
  brideId: string,
  allTableIds: string[],
  freshTablesById: Map<string, Table>,
  guestsById: Record<string, Guest>,
): void {
  for (const tableId of allTableIds) {
    const fresh = freshTablesById.get(tableId);
    if (!fresh) continue;
    purgeCoupleFromTableInTx(tx, weddingId, planId, tableId, groomId, brideId, fresh, guestsById);
  }
}

async function readTablesInTx(
  tx: Transaction,
  weddingId: string,
  planId: string,
  tableIds: string[],
): Promise<Map<string, Table>> {
  const uniqueTableIds = [...new Set(tableIds.filter((id): id is string => !!id))];
  const byId = new Map<string, Table>();
  for (const tableId of uniqueTableIds) {
    const snap = await tx.get(planTableRef(weddingId, planId, tableId));
    if (!snap.exists()) continue;
    byId.set(tableId, tableFromSnap(snap));
  }
  return byId;
}

export async function createTable(weddingId: string, planId: string, draft: TableDraft) {
  const ref = await addDoc(planTablesCol(weddingId, planId), {
    name: draft.name,
    shape: draft.shape,
    capacity: draft.capacity,
    positionX: draft.positionX,
    positionY: draft.positionY,
    rotation: draft.rotation ?? 0,
    guestIds: [],
    seatMap: [],
    hiddenSeats: draft.hiddenSeats ?? [],
    isVip: draft.isVip ?? false,
    color: draft.color ?? null,
    notes: draft.notes ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateTable(
  weddingId: string,
  planId: string,
  tableId: string,
  patch: TablePatch,
) {
  await updateDoc(planTableRef(weddingId, planId, tableId), {
    ...patch,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTable(
  weddingId: string,
  planId: string,
  tableId: string,
  seatedGuestIds: string[],
) {
  if (seatedGuestIds.length === 0) {
    await deleteDoc(planTableRef(weddingId, planId, tableId));
    return;
  }
  const batch = writeBatch(db);
  for (const gid of seatedGuestIds) {
    batch.update(guestDocRef(weddingId, gid), seatingGuestUpdate(planId, null, null));
  }
  batch.delete(planTableRef(weddingId, planId, tableId));
  await batch.commit();
}

export async function assignGuestToSeat(
  weddingId: string,
  planId: string,
  guestId: string,
  guest: Guest,
  targetTableId: string,
  targetSeatIndex: number,
  targetTable: Table,
  currentTableId: string | null,
  currentTable: Table | null,
  guestsById: Record<string, Guest>,
  allGuests: Guest[],
  allTableIds: string[],
  placeOptions: PlaceGuestOptions = {},
): Promise<void> {
  const hostGuestId = hostGuestDocId(guestId);
  assertNotLinkedCompanion(guest, allGuests);

  if (currentTableId && !currentTable) {
    throw new SeatingError("missingPreviousTable");
  }

  await runTransaction(db, async (tx) => {
    const freshTablesById = await readTablesInTx(tx, weddingId, planId, [
      ...allTableIds,
      targetTableId,
      ...(currentTableId ? [currentTableId] : []),
    ]);

    const targetRef = planTableRef(weddingId, planId, targetTableId);
    const freshTarget = freshTablesById.get(targetTableId);
    if (!freshTarget) throw new SeatingError("targetTableNotFound");

    assertSeatIndex(freshTarget, targetSeatIndex);

    const isMovingTable = currentTableId !== null && currentTableId !== targetTableId;
    let freshCurrent: Table | null = null;

    if (isMovingTable && currentTableId) {
      freshCurrent = freshTablesById.get(currentTableId) ?? null;
      if (!freshCurrent) throw new SeatingError("previousTableNotFound");
    }

    purgeGuestFromOtherTablesInTx(
      tx,
      weddingId,
      planId,
      hostGuestId,
      targetTableId,
      allTableIds,
      freshTablesById,
      guestsById,
    );

    const hidden = freshTarget.hiddenSeats ?? [];
    const baseSeatMap = effectiveSeatMap(freshTarget, guestsById);
    const occupiedKey = baseSeatMap[targetSeatIndex] ?? null;
    const isSameTable = currentTableId !== null && currentTableId === targetTableId;

    if (isSameTable && occupiedKey) {
      const sourceSeatIndex = partyHostSeatIndex(baseSeatMap, hostGuestId);
      if (sourceSeatIndex >= 0 && sourceSeatIndex !== targetSeatIndex) {
        const result = applyWithinTableOccupiedMove({
          seatMap: baseSeatMap,
          mover: guest,
          moverGuestId: hostGuestId,
          sourceSeatIndex,
          targetSeatIndex,
          hiddenSeats: hidden,
          guestsById,
        });
        if (result) {
          tx.update(targetRef, {
            seatMap: result.seatMap,
            updatedAt: serverTimestamp(),
          });
          for (const u of result.guestSeatingUpdates) {
            tx.update(
              guestDocRef(weddingId, u.guestId),
              seatingGuestUpdate(planId, targetTableId, u.seatIndex),
            );
          }
          return;
        }
        throw new SeatingError("cannotRelocateParties");
      }
    }

    // The solo-guest ↔ anonymous-plus-one swap is only sound *within* a table
    // (handled above via applyWithinTableOccupiedMove). Across tables it would
    // strand the anonymous plus-one on the source table while its host stays on
    // the target table — splitting the party and diverging seatMap/guestIds
    // (the host is never added to the source's guestIds nor removed from the
    // target's, and its seatingByPlan is left untouched). Cross-table drops
    // onto an occupied seat therefore fall through to the standard displacement
    // path below, exactly as a drop onto a host seat already does.

    const displacedGuestId =
      occupiedKey && hostGuestDocId(occupiedKey) !== hostGuestId
        ? hostGuestDocId(occupiedKey)
        : null;

    if (displacedGuestId && isSameTable) {
      throw new SeatingError("cannotRelocateParties");
    }

    const workingSeatMap = displacedGuestId
      ? removeGuestFromSeatMap(baseSeatMap, displacedGuestId)
      : baseSeatMap;

    if (
      !canPlaceParty(workingSeatMap, guest, targetSeatIndex, hidden, {
        tableShape: freshTarget.shape,
        ...placeOptions,
      })
    ) {
      throw new SeatingError("noRoomForPartyGeneric");
    }

    const newSeatMap = placeGuestInSeatMap(workingSeatMap, guest, targetSeatIndex, hidden, {
      tableShape: freshTarget.shape,
      ...placeOptions,
    });

    tx.update(
      guestDocRef(weddingId, hostGuestId),
      seatingGuestUpdate(planId, targetTableId, targetSeatIndex),
    );

    if (isMovingTable && currentTableId && freshCurrent) {
      const prevSeatMap = removeGuestFromSeatMap(
        effectiveSeatMap(freshCurrent, guestsById),
        hostGuestId,
      );
      tx.update(planTableRef(weddingId, planId, currentTableId), {
        guestIds: arrayRemove(hostGuestId),
        seatMap: prevSeatMap,
        updatedAt: serverTimestamp(),
      });
    }

    if (isMovingTable) {
      tx.update(targetRef, {
        guestIds: arrayUnion(hostGuestId),
        seatMap: newSeatMap,
        updatedAt: serverTimestamp(),
      });
    } else if (currentTableId === null) {
      tx.update(targetRef, {
        guestIds: arrayUnion(hostGuestId),
        seatMap: newSeatMap,
        updatedAt: serverTimestamp(),
      });
    } else {
      tx.update(targetRef, {
        seatMap: newSeatMap,
        updatedAt: serverTimestamp(),
      });
    }

    if (displacedGuestId) {
      tx.update(guestDocRef(weddingId, displacedGuestId), seatingGuestUpdate(planId, null, null));
      tx.update(targetRef, {
        guestIds: arrayRemove(displacedGuestId),
      });
    }
  });
}

export async function moveGuestWithinTable(
  weddingId: string,
  planId: string,
  guestId: string,
  guest: Guest,
  tableId: string,
  table: Table,
  sourceSeatIndex: number,
  targetSeatIndex: number,
  guestsById: Record<string, Guest>,
  placeOptions: PlaceGuestOptions = {},
): Promise<void> {
  const hostGuestId = hostGuestDocId(guestId);
  if (sourceSeatIndex === targetSeatIndex) return;

  await runTransaction(db, async (tx) => {
    const tableRef = planTableRef(weddingId, planId, tableId);
    const tableSnap = await tx.get(tableRef);
    if (!tableSnap.exists()) throw new SeatingError("tableNotFound");
    const freshTable = tableFromSnap(tableSnap);

    assertSeatIndex(freshTable, targetSeatIndex);
    assertSeatIndex(freshTable, sourceSeatIndex);

    const hidden = freshTable.hiddenSeats ?? [];
    const seatMap = effectiveSeatMap(freshTable, guestsById);
    const targetKey = seatMap[targetSeatIndex];

    if (!targetKey) {
      const withoutSelf = removeGuestFromSeatMap(seatMap, hostGuestId);
      if (
        !canPlaceParty(withoutSelf, guest, targetSeatIndex, hidden, {
          tableShape: freshTable.shape,
          ...placeOptions,
        })
      ) {
        throw new SeatingError("noRoomForPartyGeneric");
      }
      const newSeatMap = placeGuestInSeatMap(withoutSelf, guest, targetSeatIndex, hidden, {
        tableShape: freshTable.shape,
        ...placeOptions,
      });
      tx.update(tableRef, {
        seatMap: newSeatMap,
        updatedAt: serverTimestamp(),
      });
      tx.update(
        guestDocRef(weddingId, hostGuestId),
        seatingGuestUpdate(planId, tableId, targetSeatIndex),
      );
      return;
    }

    const hostSource =
      partyHostSeatIndex(seatMap, hostGuestId) >= 0
        ? partyHostSeatIndex(seatMap, hostGuestId)
        : sourceSeatIndex;

    const result = applyWithinTableOccupiedMove({
      seatMap,
      mover: guest,
      moverGuestId: hostGuestId,
      sourceSeatIndex: hostSource,
      targetSeatIndex,
      hiddenSeats: hidden,
      guestsById,
    });

    if (!result) {
      throw new SeatingError("cannotRelocateParties");
    }

    tx.update(tableRef, {
      seatMap: result.seatMap,
      updatedAt: serverTimestamp(),
    });
    for (const u of result.guestSeatingUpdates) {
      tx.update(
        guestDocRef(weddingId, u.guestId),
        seatingGuestUpdate(planId, tableId, u.seatIndex),
      );
    }
  });
}

export async function swapPartyMemberSeats(
  weddingId: string,
  planId: string,
  tableId: string,
  mode: SeatOccupantSwapMode,
  guestsById: Record<string, Guest>,
): Promise<void> {
  await runTransaction(db, async (tx) => {
    const tableRef = planTableRef(weddingId, planId, tableId);
    const tableSnap = await tx.get(tableRef);
    if (!tableSnap.exists()) throw new SeatingError("tableNotFound");
    const freshTable = tableFromSnap(tableSnap);
    const seatMap = effectiveSeatMap(freshTable, guestsById);
    const result =
      mode.kind === "couple"
        ? swapCoupleMemberSeatsInSeatMap(seatMap, mode.groomId, mode.brideId)
        : swapPartyMemberSeatsInSeatMap(seatMap, mode.guestId);
    if (!result) throw new SeatingError("cannotRelocateParties");
    tx.update(tableRef, {
      seatMap: result.seatMap,
      updatedAt: serverTimestamp(),
    });
    for (const u of result.guestSeatingUpdates) {
      tx.update(
        guestDocRef(weddingId, u.guestId),
        seatingGuestUpdate(planId, tableId, u.seatIndex),
      );
    }
  });
}

export async function unassignGuestWithSeatMap(
  weddingId: string,
  planId: string,
  guestId: string,
  currentTableId: string,
  currentTable: Table,
  guestsById: Record<string, Guest>,
  allTableIds: string[],
): Promise<void> {
  const hostGuestId = hostGuestDocId(guestId);
  void currentTableId;
  void currentTable;

  await runTransaction(db, async (tx) => {
    const freshTablesById = await readTablesInTx(tx, weddingId, planId, allTableIds);
    tx.update(guestDocRef(weddingId, hostGuestId), seatingGuestUpdate(planId, null, null));
    purgeGuestFromAllTablesInTx(
      tx,
      weddingId,
      planId,
      hostGuestId,
      allTableIds,
      freshTablesById,
      guestsById,
    );
  });
}

export async function assignCoupleToSeat(
  weddingId: string,
  planId: string,
  groom: Guest,
  bride: Guest,
  targetTableId: string,
  targetSeatIndex: number,
  targetTable: Table,
  groomTableId: string | null,
  groomTable: Table | null,
  brideTableId: string | null,
  brideTable: Table | null,
  guestsById: Record<string, Guest>,
  allGuests: Guest[],
  allTableIds: string[],
): Promise<void> {
  assertNotLinkedCompanion(groom, allGuests);
  assertNotLinkedCompanion(bride, allGuests);

  await runTransaction(db, async (tx) => {
    const freshTablesById = await readTablesInTx(tx, weddingId, planId, [
      ...allTableIds,
      targetTableId,
    ]);

    const targetRef = planTableRef(weddingId, planId, targetTableId);
    const freshTarget = freshTablesById.get(targetTableId);
    if (!freshTarget) throw new SeatingError("targetTableNotFound");

    assertSeatIndex(freshTarget, targetSeatIndex);

    purgeCoupleFromOtherTablesInTx(
      tx,
      weddingId,
      planId,
      groom.id,
      bride.id,
      targetTableId,
      allTableIds,
      freshTablesById,
      guestsById,
    );

    const hidden = freshTarget.hiddenSeats ?? [];
    let workingMap = effectiveSeatMap(freshTarget, guestsById);

    if (groomTableId === targetTableId || brideTableId === targetTableId) {
      workingMap = removeCoupleFromSeatMap(workingMap, groom.id, bride.id);
    }

    const occupiedKey = workingMap[targetSeatIndex] ?? null;
    if (occupiedKey) {
      const ref = parseOccupantKey(occupiedKey);
      if (ref.guestId !== groom.id) {
        throw new SeatingError("cannotRelocateParties");
      }
    }

    if (
      !canPlaceCoupleParty(workingMap, groom, bride, targetSeatIndex, hidden, freshTarget.shape)
    ) {
      throw new SeatingError("noRoomForPartyGeneric");
    }

    const newSeatMap = placeCoupleInSeatMap(
      workingMap,
      groom,
      bride,
      targetSeatIndex,
      hidden,
      freshTarget.shape,
    );
    if (!newSeatMap) throw new SeatingError("noRoomForPartyGeneric");

    const brideSeat = partyHostSeatIndex(newSeatMap, bride.id);
    if (brideSeat < 0) throw new SeatingError("noRoomForPartyGeneric");
    tx.update(
      guestDocRef(weddingId, groom.id),
      seatingGuestUpdate(planId, targetTableId, targetSeatIndex),
    );
    tx.update(
      guestDocRef(weddingId, bride.id),
      seatingGuestUpdate(planId, targetTableId, brideSeat),
    );

    const unionIds: string[] = [];
    if (groomTableId !== targetTableId) unionIds.push(groom.id);
    if (brideTableId !== targetTableId) unionIds.push(bride.id);

    const targetPatch: Record<string, unknown> = {
      seatMap: newSeatMap,
      updatedAt: serverTimestamp(),
    };
    if (unionIds.length > 0) {
      targetPatch.guestIds = arrayUnion(...unionIds);
    }
    tx.update(targetRef, targetPatch);
  });
}

export async function moveCoupleWithinTable(
  weddingId: string,
  planId: string,
  groom: Guest,
  bride: Guest,
  tableId: string,
  table: Table,
  targetSeatIndex: number,
  guestsById: Record<string, Guest>,
): Promise<void> {
  const groomHostSeat = partyHostSeatIndex(effectiveSeatMap(table, guestsById), groom.id);
  if (groomHostSeat === targetSeatIndex) return;

  await runTransaction(db, async (tx) => {
    const tableRef = planTableRef(weddingId, planId, tableId);
    const tableSnap = await tx.get(tableRef);
    if (!tableSnap.exists()) throw new SeatingError("tableNotFound");
    const freshTable = tableFromSnap(tableSnap);

    assertSeatIndex(freshTable, targetSeatIndex);

    const hidden = freshTable.hiddenSeats ?? [];
    const seatMap = effectiveSeatMap(freshTable, guestsById);
    const withoutCouple = removeCoupleFromSeatMap(seatMap, groom.id, bride.id);

    if (
      !canPlaceCoupleParty(withoutCouple, groom, bride, targetSeatIndex, hidden, freshTable.shape)
    ) {
      throw new SeatingError("cannotRelocateParties");
    }

    const newSeatMap = placeCoupleInSeatMap(
      withoutCouple,
      groom,
      bride,
      targetSeatIndex,
      hidden,
      freshTable.shape,
    );
    if (!newSeatMap) throw new SeatingError("cannotRelocateParties");

    const brideSeat = partyHostSeatIndex(newSeatMap, bride.id);
    if (brideSeat < 0) throw new SeatingError("cannotRelocateParties");
    tx.update(tableRef, {
      seatMap: newSeatMap,
      updatedAt: serverTimestamp(),
    });
    tx.update(
      guestDocRef(weddingId, groom.id),
      seatingGuestUpdate(planId, tableId, targetSeatIndex),
    );
    tx.update(guestDocRef(weddingId, bride.id), seatingGuestUpdate(planId, tableId, brideSeat));
  });
}

export async function unassignCouple(
  weddingId: string,
  planId: string,
  groom: Guest,
  bride: Guest,
  currentTableId: string,
  currentTable: Table,
  guestsById: Record<string, Guest>,
  allTableIds: string[],
): Promise<void> {
  void currentTableId;
  void currentTable;

  await runTransaction(db, async (tx) => {
    const freshTablesById = await readTablesInTx(tx, weddingId, planId, allTableIds);
    tx.update(guestDocRef(weddingId, groom.id), seatingGuestUpdate(planId, null, null));
    tx.update(guestDocRef(weddingId, bride.id), seatingGuestUpdate(planId, null, null));
    purgeCoupleFromAllTablesInTx(
      tx,
      weddingId,
      planId,
      groom.id,
      bride.id,
      allTableIds,
      freshTablesById,
      guestsById,
    );
  });
}

export interface RoomElementDraft {
  type: RoomElement["type"];
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  rotation?: number;
  label?: string | null;
}

export type RoomElementPatch = Partial<Omit<RoomElement, "id">>;

export async function createRoomElement(
  weddingId: string,
  planId: string,
  draft: RoomElementDraft,
) {
  const ref = await addDoc(planRoomElementsCol(weddingId, planId), {
    type: draft.type,
    positionX: draft.positionX,
    positionY: draft.positionY,
    width: draft.width,
    height: draft.height,
    rotation: draft.rotation ?? 0,
    label: draft.label ?? null,
  });
  return ref.id;
}

export async function updateRoomElement(
  weddingId: string,
  planId: string,
  elementId: string,
  patch: RoomElementPatch,
) {
  await updateDoc(planRoomElementRef(weddingId, planId, elementId), patch);
}

export async function deleteRoomElement(weddingId: string, planId: string, elementId: string) {
  await deleteDoc(planRoomElementRef(weddingId, planId, elementId));
}

export async function batchPreservePositions(
  weddingId: string,
  planId: string,
  tables: Table[],
  roomElements: RoomElement[],
  oldInset: SeatingCanvasInset,
  newInset: SeatingCanvasInset,
  newRoomWidthM: number,
): Promise<void> {
  const oldRoomLeft = REFERENCE_CANVAS.w * (oldInset.left / 100);
  const oldRoomTop = REFERENCE_CANVAS.h * (oldInset.top / 100);
  const oldRoomW = (REFERENCE_CANVAS.w * (100 - oldInset.left - oldInset.right)) / 100;
  const oldRoomH = (REFERENCE_CANVAS.h * (100 - oldInset.top - oldInset.bottom)) / 100;
  const newRoomLeft = REFERENCE_CANVAS.w * (newInset.left / 100);
  const newRoomTop = REFERENCE_CANVAS.h * (newInset.top / 100);
  const newRoomW = (REFERENCE_CANVAS.w * (100 - newInset.left - newInset.right)) / 100;
  const newRoomH = (REFERENCE_CANVAS.h * (100 - newInset.top - newInset.bottom)) / 100;

  const batch = writeBatch(db);

  for (const t of tables) {
    const absX = oldRoomLeft + (t.positionX / 100) * oldRoomW;
    const absY = oldRoomTop + (t.positionY / 100) * oldRoomH;
    batch.update(planTableRef(weddingId, planId, t.id), {
      positionX: ((absX - newRoomLeft) / newRoomW) * 100,
      positionY: ((absY - newRoomTop) / newRoomH) * 100,
    });
  }

  for (const el of roomElements) {
    const absX = oldRoomLeft + (el.positionX / 100) * oldRoomW;
    const absY = oldRoomTop + (el.positionY / 100) * oldRoomH;
    batch.update(planRoomElementRef(weddingId, planId, el.id), {
      positionX: ((absX - newRoomLeft) / newRoomW) * 100,
      positionY: ((absY - newRoomTop) / newRoomH) * 100,
      width: (((el.width / 100) * oldRoomW) / newRoomW) * 100,
      height: (((el.height / 100) * oldRoomH) / newRoomH) * 100,
    });
  }

  batch.update(seatingPlanRef(weddingId, planId), {
    seatingCanvasInset: newInset,
    seatingRoomWidthM: newRoomWidthM,
    updatedAt: serverTimestamp(),
  });

  await batch.commit();
}

export async function seedDefaultTables(
  weddingId: string,
  planId: string,
  count = 10,
  capacity = 8,
  tableName: (index: number) => string = (i) => `Stół ${i}`,
  options?: { canvasInset?: SeatingCanvasInset; roomWidthM?: number },
) {
  const { positions, planLayout } = computeSpaciousTableLayout(count, {
    maxCapacity: capacity,
    shape: "round",
    canvasInset: options?.canvasInset,
    roomWidthM: options?.roomWidthM ?? DEFAULT_SEATING_ROOM_WIDTH_M,
  });

  if (planLayout) {
    await updateSeatingPlan(weddingId, planId, {
      seatingCanvasInset: planLayout.seatingCanvasInset,
      seatingRoomWidthM: planLayout.seatingRoomWidthM,
    });
  }

  for (let i = 0; i < count; i++) {
    const { positionX, positionY } = positions[i]!;
    await createTable(weddingId, planId, {
      name: tableName(i + 1),
      shape: "round",
      capacity,
      positionX,
      positionY,
    });
  }
}

/** Unassign guest from all plans they appear in (soft-delete cleanup). */
export async function unassignGuestFromAllPlans(
  weddingId: string,
  guest: Guest,
  plansTables: Array<{ planId: string; tables: Table[] }>,
  guestsById: Record<string, Guest>,
): Promise<void> {
  const planIds = Object.keys(guest.seatingByPlan ?? {});
  if (planIds.length === 0 && !guest.tableId) return;

  for (const { planId, tables } of plansTables) {
    const seating = guest.seatingByPlan?.[planId];
    const tableId =
      seating?.tableId ?? (guest.tableId && planIds.length === 0 ? guest.tableId : null);
    if (!tableId) continue;
    const table = tables.find((t) => t.id === tableId);
    if (table) {
      const allTableIds = tables.map((t) => t.id);
      await unassignGuestWithSeatMap(
        weddingId,
        planId,
        guest.id,
        tableId,
        table,
        guestsById,
        allTableIds,
      );
    }
  }
}

export type TableDeleteSnapshot = {
  draft: TableDraft;
  seatMap: (string | null)[];
  guestIds: string[];
};

/** Recreate a deleted table and restore seat assignments (new table id). */
export async function restoreDeletedTable(
  weddingId: string,
  planId: string,
  snapshot: TableDeleteSnapshot,
): Promise<string> {
  const newId = await createTable(weddingId, planId, snapshot.draft);
  if (snapshot.guestIds.length === 0 && snapshot.seatMap.every((k) => !k)) {
    return newId;
  }

  await runTransaction(db, async (tx) => {
    const tableRef = planTableRef(weddingId, planId, newId);
    tx.update(tableRef, {
      guestIds: snapshot.guestIds,
      seatMap: snapshot.seatMap,
      updatedAt: serverTimestamp(),
    });
    for (const guestId of snapshot.guestIds) {
      const seatIndex = hostSeatIndexInMap(snapshot.seatMap, guestId);
      if (seatIndex < 0) continue;
      tx.update(guestDocRef(weddingId, guestId), seatingGuestUpdate(planId, newId, seatIndex));
    }
  });

  return newId;
}

/** Recreate a deleted room element (new element id). */
export async function restoreRoomElement(
  weddingId: string,
  planId: string,
  element: RoomElement,
): Promise<string> {
  return createRoomElement(weddingId, planId, {
    type: element.type,
    positionX: element.positionX,
    positionY: element.positionY,
    width: element.width,
    height: element.height,
    rotation: element.rotation,
    label: element.label,
  });
}

function hostSeatIndexInMap(seatMap: (string | null)[], guestId: string): number {
  return partyHostSeatIndex(seatMap, guestId);
}

/** Restore merged seat state captured before a mutation (undo). */
export async function applySeatSnapshotToFirestore(
  weddingId: string,
  planId: string,
  before: OptimisticSeatPatch,
  guestsById: Record<string, Guest>,
): Promise<void> {
  const tableIds = Object.keys(before.seatMaps);
  if (tableIds.length === 0) return;

  await runTransaction(db, async (tx) => {
    const freshTables = new Map<string, Table>();
    for (const tid of tableIds) {
      const ref = planTableRef(weddingId, planId, tid);
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new SeatingError("targetTableNotFound");
      freshTables.set(tid, tableFromSnap(snap));
    }

    const guestIdsToUpdate = new Set<string>(Object.keys(before.guestSeating).map(hostGuestDocId));
    for (const tid of tableIds) {
      const fresh = freshTables.get(tid)!;
      for (const gid of fresh.guestIds) guestIdsToUpdate.add(hostGuestDocId(gid));
      const map = before.seatMaps[tid] ?? [];
      for (const key of map) {
        if (!key) continue;
        guestIdsToUpdate.add(hostGuestDocId(key));
      }
    }

    for (const tid of tableIds) {
      const seatMap = before.seatMaps[tid]!;
      tx.update(planTableRef(weddingId, planId, tid), {
        seatMap,
        guestIds: hostGuestIdsInSeatMap(seatMap),
        updatedAt: serverTimestamp(),
      });
    }

    for (const gid of guestIdsToUpdate) {
      const guest = guestsById[gid];
      if (!guest) continue;

      const explicit = before.guestSeating[gid];
      if (explicit === null) {
        tx.update(guestDocRef(weddingId, gid), seatingGuestUpdate(planId, null, null));
        continue;
      }
      if (explicit) {
        tx.update(
          guestDocRef(weddingId, gid),
          seatingGuestUpdate(planId, explicit.tableId, explicit.seatIndex),
        );
        continue;
      }

      let restored = false;
      for (const tid of tableIds) {
        const map = before.seatMaps[tid] ?? [];
        const seatIndex = hostSeatIndexInMap(map, gid);
        if (seatIndex >= 0) {
          tx.update(guestDocRef(weddingId, gid), seatingGuestUpdate(planId, tid, seatIndex));
          restored = true;
          break;
        }
      }
      if (!restored) {
        const current = guest.seatingByPlan?.[planId];
        if (current?.tableId && tableIds.includes(current.tableId)) {
          tx.update(guestDocRef(weddingId, gid), seatingGuestUpdate(planId, null, null));
        }
      }
    }
  });
}
